import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Body2,
  Button,
  Card,
  CardHeader,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Input,
  Link,
  mergeClasses,
  Persona,
  Skeleton,
  SkeletonItem,
  Text,
  Tooltip,
  useId,
} from "@fluentui/react-components";
import { Dismiss16Regular } from "@fluentui/react-icons";
import { ConstantValues } from "common/constants";
import { CustomDialog } from "components/customDialog/CustomDialog";
import { FileWithId } from "models/FileWithId";
import { IStandardChartData } from "models/IChartData";
import { ICitation } from "models/ICitation";
import { IFile } from "models/IFile";
import { IMessage } from "models/IMessage";
import { IAIResponseError } from "models/IResponseError";
import { ITag } from "models/ITag";
import { IUser } from "models/IUser";
import { useEffect, useRef, useState } from "react";
import { useMutation } from "react-query";
import { useSelector } from "react-redux";
import { RootState } from "store";
import { downloadFile, uploadFilesToStorage } from "utils/blobUtils";
import {
  buildCitationKeyMap,
  renderContentWithCitations,
} from "utils/generalUtils";
import { resolveAssistantLlmType } from "utils/llmModelMigration";
import { v4 as uuidv4 } from "uuid";
import { getAIResponse, getCategories } from "../../api";
import { resolveAsset } from "../../common/fileIcons";
import { getChunkAndUpdateAIMessage } from "../../common/streamProcessing.js";
import { FilePreview } from "../chat/filePreview/FilePreview";
import { MessageInput } from "../chat/messageInput/MessageInput";
import { useStyles } from "./HelpChat.styles";
import { IHelpChatProps } from "./HelpChat.types";

import { useIsMobile } from "hooks/useIsMobile";
import { ICategory } from "models/ICategory";
import { useDispatch } from "react-redux";
import {
  useSharedButtonStyles,
  useSharedCardStyles,
  useSharedCloseButtonStyles,
  useSharedModalStyles,
  useSharedTextStyles,
} from "theme/sharedStyles";
import { setCurrentFiles } from "../../redux/files";
import { setTagsForKey } from "../../redux/tags";

export const HelpChat = ({
  isOpen,
  onDismiss,
}: IHelpChatProps): JSX.Element => {
  const styles = useStyles();
  const sharedButtonStyles = useSharedButtonStyles();
  const sharedCardStyles = useSharedCardStyles();
  const sharedCloseButtonStyles = useSharedCloseButtonStyles();
  const sharedTextStyles = useSharedTextStyles();
  const sharedModalStyles = useSharedModalStyles();
  const dispatch = useDispatch();

  const isMobile = useIsMobile();

  const currentUser: IUser = useSelector(
    (state: RootState) => state.currentUserSlice.value,
  );

  const availableModels = useSelector(
    (state: RootState) => state.accessLLMModelsSlice.value,
  );

  const helpChatTags = useSelector(
    (state: RootState) => state.currentTagsSlice.tags["currentTags"] || [],
  );

  const [messages, setMessages] = useState<IMessage[]>([]);
  const [text, setText] = useState<string>(ConstantValues.EMPTY_STRING);
  const [aiGenerateMessage, setAiGenerateMessage] = useState<string>(
    ConstantValues.EMPTY_STRING,
  );
  const [isStreaming, setIsStreaming] = useState<boolean>(false);
  const [uploadFiles, setUploadFiles] = useState<FileWithId[]>([]);
  const [editMessageId, setEditMessageId] = useState<string | null>(null);
  const [editMessageValue, setEditMessageValue] = useState<string | null>(null);
  const [autoScrollEnabled, setAutoScrollEnabled] = useState<boolean>(true);
  const [unsupportedFileMessage, setUnsupportedFileMessage] = useState<
    string | null
  >(null);

  const editMessageInputId: string = useId("input");
  const messagesEndRef: React.MutableRefObject<HTMLDivElement | null> =
    useRef<HTMLDivElement | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const helpChatLLMConfig = {
    // Re-evaluated every render, so it follows the models once they load.
    llmType: resolveAssistantLlmType(availableModels),
    rag: false,
    citate: false,
    sourceRagCategories: [],
    defaultKnowledgeFile: "helppageknowledge/vectorstore.tar.gz",
    twoWayConversationSetting: true,
  };

  useEffect(() => {
    const fetchHelpCategories = async () => {
      try {
        const categories = await getCategories(true);
        const helpTags: ITag[] = [];

        categories?.forEach((category: ICategory) => {
          if (category.title && category.title.includes("AIMS Help")) {
            if (category.tags) {
              category.tags.forEach((tag: ITag) => {
                helpTags.push({
                  ...tag,
                });
              });
            }
          }
        });

        if (helpTags.length > 0) {
          dispatch(setTagsForKey({ key: "currentTags", tags: helpTags }));
        } else {
          dispatch(setTagsForKey({ key: "currentTags", tags: [] }));
        }
      } catch (error) {
        console.error("Error fetching help categories:", error);
      }
    };

    if (isOpen) {
      if (helpChatTags.length === 0) {
        fetchHelpCategories();
      }
      if (messages.length === 0) {
        const initialHelpMessage: IMessage = {
          id: uuidv4(),
          content:
            "Hello! I'm here to help you with AIMS, AI Interviews or AI Analytics.",
          files: [],
          citations: [],
          isHidden: false,
          role: "assistant",
          tags: helpChatTags,
          sourceDocuments: [],
          llmConfig: helpChatLLMConfig,
          createdAt: Date.now(),
        };
        setMessages([initialHelpMessage]);
      }
    }
  }, [isOpen, dispatch, helpChatTags.length]);

  useEffect((): void => {
    if (messages.length % 2 === 1 && autoScrollEnabled) {
      scrollToBottom();
    }
  }, [messages, aiGenerateMessage, autoScrollEnabled]);

  useEffect((): void => {
    if (messages.length % 2 !== 0 && messages.length > 1) {
      handleUserMessage();
    }
  }, [messages]);

  useEffect(() => {
    const container: HTMLDivElement | null = messagesContainerRef.current;

    if (container) {
      container.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (container) {
        container.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  const handleScroll = (): void => {
    if (!messagesContainerRef.current) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } =
      messagesContainerRef.current;
    if (scrollHeight - scrollTop <= clientHeight + 50) {
      setAutoScrollEnabled(true);
      return;
    }

    setAutoScrollEnabled(false);
  };

  const scrollToBottom = (): void => {
    if (!autoScrollEnabled) {
      return;
    }

    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleUserMessage = (): void => {
    setAutoScrollEnabled(true);
    scrollToBottom();
  };

  const buildFakeMessage = ({
    text,
    role,
    chartData,
    sourceDocuments,
    files,
    citations,
  }: {
    text: any;
    role: any;
    chartData: IStandardChartData | undefined;
    sourceDocuments: any;
    files: any;
    citations: ICitation[];
  }): IMessage => {
    return {
      id: uuidv4(),
      content: text,
      citations: citations,
      files: files,
      isHidden: false,
      chartData: chartData,
      role: role,
      tags: helpChatTags,
      sourceDocuments: sourceDocuments,
      llmConfig: helpChatLLMConfig,
      createdAt: Math.floor(Date.now() / 1000),
    };
  };

  const chatWithAi = async () => {
    if (!text || !text.trim()) {
      setText(ConstantValues.EMPTY_STRING);
      return;
    }

    setIsStreaming(true);
    setAiGenerateMessage(ConstantValues.EMPTY_STRING);

    const responseNames: IFile[] = await uploadFilesToStorage(
      uploadFiles,
      "user_store",
      currentUser.id ?? ConstantValues.EMPTY_STRING,
      true,
    );

    const userMessage = buildFakeMessage({
      text: text,
      role: "user",
      chartData: undefined,
      sourceDocuments: [],
      citations: [],
      files: responseNames,
    });

    let newMessages: IMessage[] = [...messages, userMessage];
    setMessages(newMessages);
    setText(ConstantValues.EMPTY_STRING);
    setUploadFiles([]);
    dispatch(setCurrentFiles([]));

    const response = await (
      getAIResponse as (...args: unknown[]) => Promise<unknown>
    )({
      llmConfig: helpChatLLMConfig,
      messages: newMessages,
      persistFiles: false,
    });

    if ("error" in (response as Record<string, unknown>)) {
      setIsStreaming(false);
      throw new Error(
        `AI Response Error: ${(response as any).status || "Unknown"} - ${(response as any).message || "Something went wrong"}`,
      );
    }

    const { content, chartData, sourceDocuments, citations } =
      await getChunkAndUpdateAIMessage(
        response,
        (response as Response).body,
        null,
        setAiGenerateMessage as any,
        null,
        dispatch,
      );

    setIsStreaming(false);
    setAiGenerateMessage(ConstantValues.EMPTY_STRING);

    if (!content) {
      return;
    }

    const aiMessage: IMessage = buildFakeMessage({
      text: content,
      role: "assistant",
      chartData,
      sourceDocuments,
      files: responseNames,
      citations,
    });

    newMessages = [...newMessages, aiMessage];
    setMessages(newMessages);
    return response;
  };

  const chatWithAiMutation = useMutation({
    mutationFn: chatWithAi,
    onSuccess: () => {
      setAiGenerateMessage(ConstantValues.EMPTY_STRING);
      setText(ConstantValues.EMPTY_STRING);
      setUploadFiles([]);
      setIsStreaming(false);
    },
    onError: (error: IAIResponseError) => {
      setIsStreaming(false);
      setAiGenerateMessage(ConstantValues.EMPTY_STRING);
      const errorMessage: string =
        error?.message || "Something went wrong. Please try again later.";
      const errorContent: string = `**Error:** ${errorMessage}\n\nIf this issue persists, please [contact support](mailto:nhhelpu@northhighland.com?subject=AIMS%20Help%20Chat%20Error).`;
      const newAIMessage: IMessage = {
        id: uuidv4(),
        content: errorContent,
        files: [],
        citations: [],
        isHidden: false,
        role: "assistant",
        tags: helpChatTags,
        sourceDocuments: [],
        llmConfig: helpChatLLMConfig,
        createdAt: Date.now(),
      };
      setMessages([...messages, newAIMessage]);
    },
  });

  const setEditMessage = (message: IMessage) => (): void => {
    setEditMessageValue(message?.content);
    setEditMessageId(message?.id ?? ConstantValues.EMPTY_STRING);
  };

  const desetEditMessage = () => (): void => {
    setEditMessageValue(null);
    setEditMessageId(null);
  };

  const updateValueOfEditingMessage = (ev: any, data: any): void => {
    setEditMessageValue(data?.value);
  };

  const onClickMessageBar = (): void => {
    chatWithAiMutation.mutate();
  };

  const onEnterPress = async (e: any) => {
    if (e.keyCode === 13 && e.shiftKey === false) {
      e.preventDefault();
      if (chatWithAiMutation.isLoading) return;
      chatWithAi();
    }
  };

  const deselectFile = (file: FileWithId): void => {
    const remainingFiles: FileWithId[] = uploadFiles.filter(
      (f: FileWithId): boolean => f.file.name !== file.file.name,
    );
    setUploadFiles(remainingFiles);
  };

  const redoConversationFromEditedMessage =
    (lastMessageRemaining: IMessage) => () => {
      const reaminingMessages = [];
      for (const message of messages) {
        if ((message as any)?.id === lastMessageRemaining?.id) {
          break;
        }

        reaminingMessages.push(message);
      }

      lastMessageRemaining.content =
        editMessageValue ?? ConstantValues.EMPTY_STRING;
      reaminingMessages.push(lastMessageRemaining);

      setEditMessageValue(null);
      setEditMessageId(null);
      setMessages(reaminingMessages);
      chatWithAiMutation.mutate();
    };

  const resetHelp = (): void => {
    setText(ConstantValues.EMPTY_STRING);
    setMessages([]);
    setUploadFiles([]);
    onDismiss();
  };
  if (!isOpen) {
    return <></>;
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(event, data) => {
        if (!data.open && event.type === "keydown") {
          resetHelp();
        }
      }}
      modalType="modal"
    >
      <DialogSurface
        className={isMobile ? styles.dialogSurfaceMobile : styles.dialogSurface}
      >
        <DialogBody className={styles.dialogBody}>
          <DialogTitle
            className={mergeClasses(
              sharedModalStyles.modalHeader,
              styles.dialogTitle,
            )}
          >
            <div className={styles.headerContainer}>
              <div className={styles.headerLeft}>
                <Text className={sharedTextStyles.heading3}>Help</Text>
              </div>
              <Tooltip content="Exit Help" relationship="label">
                <Button
                  appearance="transparent"
                  aria-label="Exit Help"
                  className={sharedCloseButtonStyles.closeButton}
                  icon={<Dismiss16Regular />}
                  onClick={resetHelp}
                />
              </Tooltip>
            </div>
          </DialogTitle>
          <DialogContent className={styles.dialogContent}>
            <div ref={messagesContainerRef} className={styles.messagesArea}>
              <div className={styles.messagesContainer}>
                {messages?.map((message: IMessage, index: number) =>
                  message?.id !== editMessageId ? (
                    <div
                      key={index}
                      className={mergeClasses(
                        styles.messageDetails,
                        message?.role === "assistant"
                          ? styles.assistantMessage
                          : styles.userMessage,
                      )}
                    >
                      <Persona
                        name={
                          message?.role === "assistant"
                            ? "NH AIMS Help"
                            : currentUser?.displayName
                        }
                        secondaryText={message?.role}
                        size="large"
                        avatar={
                          message?.role === "assistant"
                            ? { image: { src: "/images/NH_Logo_Circle.png" } }
                            : { name: currentUser?.displayName }
                        }
                      />
                      <Body2
                        className={mergeClasses(
                          styles.messageContent,
                          message?.role === "assistant"
                            ? styles.assistantMessageContent
                            : styles.userMessageContent,
                        )}
                      >
                        {renderContentWithCitations(
                          message.content,
                          message.citations,
                        )}
                      </Body2>
                      <div className={styles.filesColumn}>
                        {message?.files?.map((file: IFile, index: number) => (
                          <Card
                            key={index}
                            className={styles.fileCardFit}
                            size="small"
                            role="listitem"
                          >
                            <CardHeader
                              image={{
                                as: "img",
                                src: resolveAsset(
                                  decodeURI(
                                    file?.filename?.split("/")?.at(-1) ||
                                      ConstantValues.EMPTY_STRING,
                                  ),
                                  file,
                                ),
                                alt: "File icon",
                                width: "40px",
                                height: "40px",
                              }}
                              header={
                                <Text size={100} weight="semibold">
                                  {decodeURI(
                                    file?.filename?.split("/")?.at(-1) ||
                                      ConstantValues.EMPTY_STRING,
                                  )}
                                </Text>
                              }
                            />
                          </Card>
                        ))}
                      </div>

                      {message?.sourceDocuments &&
                        message.sourceDocuments.length > 0 && (
                          <>
                            {message.sourceDocuments
                              .slice(0, 2)
                              .map((document: any, index: number) => {
                                const { citationKeyMap } = buildCitationKeyMap(
                                  message.citations ?? [],
                                );

                                const matchingCitations =
                                  message.citations?.filter(
                                    (c: ICitation) =>
                                      c.file_name === document.filename,
                                  ) ?? [];

                                let citationId: number | null = null;
                                for (const citation of matchingCitations) {
                                  const key = `${citation.file_name}::${citation.text.trim()}`;
                                  const id = citationKeyMap.get(key);
                                  if (
                                    id !== undefined &&
                                    (citationId === null || id < citationId)
                                  ) {
                                    citationId = id;
                                  }
                                }

                                return (
                                  <Card
                                    key={index}
                                    className={`${sharedCardStyles.card} ${styles.sourceDocCard}`}
                                  >
                                    <CardHeader
                                      image={{
                                        as: "img",
                                        src: resolveAsset(
                                          document?.filename,
                                          document,
                                        ),
                                        alt: "Document icon",
                                        width: "40px",
                                        height: "40px",
                                      }}
                                      header={
                                        <div>
                                          <Link
                                            onClick={downloadFile(
                                              document.filename ??
                                                ConstantValues.EMPTY_STRING,
                                            )}
                                            title={document?.filename}
                                            className={styles.cursorPointer}
                                          >
                                            {document?.filename}
                                          </Link>
                                          {citationId && (
                                            <Text
                                              className={`${sharedTextStyles.caption} ${styles.citationText}`}
                                            >
                                              [Citation ID: {citationId}]
                                            </Text>
                                          )}
                                        </div>
                                      }
                                    />
                                    <p
                                      className={`${sharedTextStyles.body} ${styles.sourceDocContent}`}
                                    >
                                      {document?.content}
                                    </p>
                                  </Card>
                                );
                              })}
                            {message.sourceDocuments.length > 2 && (
                              <Accordion collapsible>
                                <AccordionItem value="additionalSourceDocs">
                                  <AccordionHeader>
                                    +{message.sourceDocuments.length - 2} more
                                  </AccordionHeader>
                                  <AccordionPanel>
                                    {message.sourceDocuments
                                      .slice(2)
                                      .map((document: any, index: number) => {
                                        const { citationKeyMap } =
                                          buildCitationKeyMap(
                                            message.citations ?? [],
                                          );

                                        const matchingCitations =
                                          message.citations?.filter(
                                            (c: ICitation) =>
                                              c.file_name === document.filename,
                                          ) ?? [];

                                        let citationId: number | null = null;
                                        for (const citation of matchingCitations) {
                                          const key = `${citation.file_name}::${citation.text.trim()}`;
                                          const id = citationKeyMap.get(key);
                                          if (
                                            id !== undefined &&
                                            (citationId === null ||
                                              id < citationId)
                                          ) {
                                            citationId = id;
                                          }
                                        }

                                        return (
                                          <Card
                                            key={index}
                                            className={`${sharedCardStyles.card} ${styles.sourceDocCard}`}
                                          >
                                            <CardHeader
                                              image={{
                                                as: "img",
                                                src: resolveAsset(
                                                  document?.filename,
                                                  document,
                                                ),
                                                alt: "Document icon",
                                                width: "40px",
                                                height: "40px",
                                              }}
                                              header={
                                                <div>
                                                  <Link
                                                    onClick={downloadFile(
                                                      document.filename ??
                                                        ConstantValues.EMPTY_STRING,
                                                    )}
                                                    title={document?.filename}
                                                    className={
                                                      styles.cursorPointer
                                                    }
                                                  >
                                                    {document?.filename}
                                                  </Link>
                                                  {citationId && (
                                                    <Text
                                                      className={`${sharedTextStyles.caption} ${styles.citationText}`}
                                                    >
                                                      [Citation ID: {citationId}
                                                      ]
                                                    </Text>
                                                  )}
                                                </div>
                                              }
                                            />
                                            <p
                                              className={`${sharedTextStyles.body} ${styles.sourceDocContent}`}
                                            >
                                              {document?.content}
                                            </p>
                                          </Card>
                                        );
                                      })}
                                  </AccordionPanel>
                                </AccordionItem>
                              </Accordion>
                            )}
                          </>
                        )}
                    </div>
                  ) : (
                    <div
                      key={index}
                      className={`${styles.messageDetails} ${
                        message?.role === "assistant"
                          ? styles.assistantMessage
                          : styles.userMessage
                      }`}
                    >
                      <Persona
                        name={
                          message?.role === "assistant"
                            ? "AIMS Help Assistant"
                            : currentUser?.displayName
                        }
                        secondaryText={message?.role}
                        size="large"
                        avatar={
                          message?.role === "assistant"
                            ? { image: { src: "/images/NH_Logo_Circle.png" } }
                            : { name: currentUser?.displayName }
                        }
                      />
                      <Input
                        value={editMessageValue ?? ConstantValues.EMPTY_STRING}
                        id={editMessageInputId}
                        onChange={updateValueOfEditingMessage}
                        className={styles.editInput}
                      />

                      <div className={styles.editActions}>
                        <Button
                          appearance="subtle"
                          className={sharedButtonStyles.secondaryButton}
                          onClick={desetEditMessage()}
                          size="small"
                        >
                          Cancel
                        </Button>
                        <Button
                          appearance="primary"
                          className={sharedButtonStyles.primaryButton}
                          size="small"
                          onClick={redoConversationFromEditedMessage(message)}
                        >
                          Save
                        </Button>
                      </div>
                    </div>
                  ),
                )}

                {isStreaming && (
                  <div
                    className={`${styles.messageDetails} ${styles.assistantMessage}`}
                  >
                    <Persona
                      name="AIMS Help Assistant"
                      secondaryText="assistant"
                      size="large"
                      avatar={{ image: { src: "/images/NH_Logo_Circle.png" } }}
                    />
                    {aiGenerateMessage && aiGenerateMessage?.length > 0 ? (
                      <Body2 className={styles.messageContent}>
                        {renderContentWithCitations(aiGenerateMessage, [])}
                      </Body2>
                    ) : (
                      <Body2 className={styles.messageContent}>
                        <Skeleton aria-label="Loading response">
                          <div className={styles.skeletonContent}>
                            <SkeletonItem
                              animation="wave"
                              size={8}
                              style={{ width: "80%" }}
                            />
                            <SkeletonItem
                              animation="wave"
                              size={8}
                              style={{ width: "60%" }}
                            />
                            <SkeletonItem
                              animation="wave"
                              size={8}
                              style={{ width: "20%" }}
                            />
                          </div>
                        </Skeleton>
                      </Body2>
                    )}
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>
            </div>
          </DialogContent>

          <div className={styles.mainWrapper}>
            <div className={styles.mainContainer}>
              <FilePreview files={uploadFiles} onRemove={deselectFile} />

              <div className={styles.fieldContainer}>
                <CustomDialog
                  isOpen={!!unsupportedFileMessage}
                  onClose={() => setUnsupportedFileMessage(null)}
                  title="Unsupported File Type"
                >
                  <p>{unsupportedFileMessage}</p>
                </CustomDialog>

                <div className={styles.inputContainer}>
                  <MessageInput
                    id="help-chat"
                    text={text}
                    setText={setText}
                    setUnsupportedFileMessage={setUnsupportedFileMessage}
                    onKeyPress={onEnterPress}
                    onSend={onClickMessageBar}
                    disableDragDrop
                    disabled={chatWithAiMutation.isLoading}
                    setUploadFiles={setUploadFiles}
                  />
                </div>

                <div className={styles.helperText}>
                  Enter to send, Shift+Enter for new line
                </div>
              </div>
            </div>
          </div>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
